import { AdditiveBlending, type Scene } from 'three';
import { VisualRandom } from '../../visual/index.js';
import type {
  QualityContext,
  WaveEvent,
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
} from '../../visual/index.js';
import { InstancedField, NOISE_GLSL, blendingFor } from './materials/InstancedField.js';

/**
 * A propagating wave field.
 *
 * This used to be a pool of `LineLoop`s: an outline was spawned, its radius was
 * increased every frame, and it faded out. That draws a *ring that gets bigger*,
 * which is not what a wave is. A wave has a front that travels at a fixed speed,
 * a train of crests behind it at a fixed spacing, an amplitude that falls as the
 * energy spreads around a growing circumference, and — the part no stack of
 * outlines can ever produce — it **adds to other waves**, crest to crest and
 * crest to trough.
 *
 * So the geometry is one quad per event and the field is evaluated in the
 * fragment, which makes all six of the things a wave does available at once:
 *
 *   - **Propagation.** The front moves outward at a speed set when the event
 *     fired, in world units per second, and does not depend on the frame rate or
 *     on how long the event has been alive.
 *   - **Phase.** Crests are `sin` of distance behind the front, so their spacing
 *     is the wavelength and they move *through* the envelope as it expands
 *     rather than moving with it. A ring cannot show this; it has one edge.
 *   - **Deformation.** The medium is not uniform. The distance each source is
 *     measured at is warped by noise that grows with radius, so a wave that has
 *     travelled further arrives more distorted than one that has just left.
 *   - **Thickness evolution.** Crests start narrow and broaden as the train
 *     ages, which is what dispersion looks like from above.
 *   - **Interference.** Each event has *two* sources a short distance apart and
 *     sums their signed displacement before anything is drawn, so the field has
 *     true nodal lines — places where two crests cancel and nothing is drawn at
 *     all. Summing intensities, which is what additive outlines do, can only
 *     ever brighten; it has no way to produce a cancellation.
 *   - **A secondary wave.** A slower, longer swell trails the leading train at
 *     roughly half its frequency, the way a real disturbance leaves a following
 *     wave behind its first.
 *
 * It is still intentionally *not* the gameplay ripple field. `RippleField` draws
 * the tappable wave seals and is judged against; this draws a medium that cannot
 * be hit and carries no timing meaning.
 */

/** How a wave event was made — the "different material" of §12. */
type Kind = 'clean' | 'torn' | 'spiral' | 'fracture';

const KIND_CODE: Readonly<Record<Kind, number>> = {
  clean: 0,
  torn: 1,
  spiral: 2,
  fracture: 3,
};

const WAVE_FRAGMENT = /* glsl */ `
precision highp float;

${NOISE_GLSL}

uniform float uTime;
uniform float uDetail;
uniform float uSharpness;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

/**
 * One wave train, as signed displacement.
 *
 * 'u' is how far behind the front the sample is. Ahead of the front there is
 * nothing — the disturbance has not arrived yet, and that hard leading boundary
 * is most of what makes this read as something travelling rather than as
 * something growing.
 */
float train(float u, float k, float phase, float length_) {
  if (u < 0.0) return 0.0;
  return sin(u * k * 6.28318 + phase) * exp(-u / max(length_, 0.02));
}

void main() {
  vec2 p = (vUv - 0.5) * 2.0;
  float seed = vLife.z;
  float kind = vLife.w;
  float k = vShape.x;
  float sep = vShape.y;
  float deform = vShape.z;
  float trainLength = vShape.w;

  float rr = length(p);

  // The medium. Distortion scales with radius, so the wave leaves clean and
  // arrives bent — a constant warp would just be a wobbly ring.
  float warp = (fbm(p * 2.1 + seed * 7.0, int(uDetail)) - 0.5) * deform * rr;

  // A spiral event twists the phase with the angle, so its crests wind outward
  // instead of closing. This is the off-beat mark, and it is the one shape here
  // that is not radially symmetric.
  float twist = kind > 1.5 && kind < 2.5 ? atan(p.y, p.x) * 0.5 : 0.0;

  // The front sits inside the quad, leaving the outer tenth for it to fade
  // through: anything reaching the edge would come back cut off square.
  float front = 0.9;

  // Two sources. Their separation is what puts nodal lines in the field.
  float r1 = length(p - vec2(sep, 0.0)) + warp;
  float r2 = length(p + vec2(sep, 0.0)) + warp;

  float lead = train(front - r1, k, twist, trainLength)
             + train(front - r2, k, twist, trainLength);

  // The swell that follows: longer, slower, and set back from the leading edge.
  float swell = 0.55 * (train(front - r1 - 0.14, k * 0.45, 1.2 + twist, trainLength * 1.8)
                      + train(front - r2 - 0.14, k * 0.45, 1.2 + twist, trainLength * 1.8));

  // Signed, so two crests meeting cancel where they are out of phase.
  float w = lead + swell;

  // A fracture has lost part of its front; it arrives in pieces.
  if (kind > 2.5) {
    float gap = fbm(vec2(atan(p.y, p.x) * 1.6, seed * 3.0), int(uDetail));
    w *= smoothstep(0.32, 0.55, gap);
  }

  // Thickness evolution. Young crests are narrow, old ones broad — the exponent
  // is what sets how much of the cycle is above the visible threshold.
  float sharp = mix(3.2, 1.1, vAge) * uSharpness;
  float energy = pow(clamp(abs(w) * 0.62, 0.0, 1.0), sharp);

  // Nothing ahead of the front, and nothing left at the centre: the energy that
  // was there has already travelled outward.
  energy *= 1.0 - smoothstep(front, 1.0, rr);
  energy *= smoothstep(0.0, 0.14, rr);
  if (energy <= 0.0) discard;

  // Crests catch more light than troughs.
  vec3 tone = vColor.rgb * mix(0.62, 1.0, step(0.0, w));

  float alpha = clamp(energy * vColor.a, 0.0, 1.0);
  if (alpha < 0.004) discard;

  gl_FragColor = vec4(tone, alpha);
}
`;

/** One live wave event. Pooled: a run spawns hundreds and must not allocate. */
interface Wave {
  active: boolean;
  x: number;
  y: number;
  born: number;
  lifetime: number;
  /** Where the front has reached, in world units. */
  radius: number;
  /** How fast it travels, in world units per second. Constant for its life. */
  speed: number;
  /** Amplitude at the radius it started from. Energy spreads from there. */
  amplitude: number;
  startRadius: number;
  /** Crests per quad radius. */
  wavenumber: number;
  /** How far apart this event's two sources are. */
  separation: number;
  deform: number;
  trainLength: number;
  hue: number;
  kind: Kind;
  seed: number;
}

export interface RippleArtRendererOptions {
  readonly scene: Scene;
  readonly layerId: string;
  readonly fieldRadius: number;
  /** Hard cap on live wave events. The quality control for this renderer. */
  readonly capacity?: number;
}

export class RippleArtRenderer implements VisualRenderer {
  readonly id = 'RIPPLE';

  private readonly scene: Scene;
  private readonly fieldRadius: number;
  private readonly baseCapacity: number;

  /**
   * Live capacity, after the quality preset has had its say.
   *
   * Read when the field is built rather than stored as a constant, so an offline
   * render at ULTRA gets four times the events a live run does — from the same
   * recipe, with no recipe change.
   */
  private capacity: number;
  private detail = 3;
  private sharpness = 1;

  private field: InstancedField | null = null;
  private waves: Wave[] = [];
  private enabled = true;
  private spawnAccumulator = 0;
  private pendingSpawns: {
    strength: number;
    kind: Kind;
    /** Field units. Absent means "wherever this renderer likes". */
    at?: { x: number; y: number };
    /** A wave's own stream, so its front does not depend on the layer's. */
    stream?: VisualRandom;
    scale?: number;
  }[] = [];
  private wavesDrawn = 0;
  private parameters: Readonly<Record<string, number | string | boolean>> = {};
  private lastTime = 0;

  /**
   * Accumulated damage, which is the point of Phase 3.
   *
   * `DISTORT` adds to this and it decays slowly, so one miss bends the next few
   * wavefronts and five misses in a row bend everything for a while. The run's
   * history is visible in the medium rather than only in the score.
   */
  private accumulatedDistortion = 0;
  private radiusMultiplier = 1;
  private frozen = 0;
  /** Counters the diagnostics and tests read to prove a mutation landed. */
  private readonly mutationCounts: Record<string, number> = {};

  constructor(private readonly options: RippleArtRendererOptions) {
    this.scene = options.scene;
    this.fieldRadius = options.fieldRadius;
    this.baseCapacity = options.capacity ?? 48;
    this.capacity = this.baseCapacity;
  }

  initialize(context: VisualFrameContext): void {
    if (this.field === null) {
      this.field = new InstancedField({
        scene: this.scene,
        name: `visual:RIPPLE:${this.options.layerId}`,
        capacity: this.capacity,
        fragmentShader: WAVE_FRAGMENT,
        // Waves on a dark surface are light, and light from two disturbances
        // does add. The cancellation that matters happens *before* this, inside
        // one event's own field, where the displacement is still signed.
        blending: AdditiveBlending,
        renderOrder: 10,
        uniforms: {
          uDetail: { value: this.detail },
          uSharpness: { value: this.sharpness },
        },
      });
      this.waves = Array.from({ length: this.capacity }, () => emptyWave());
    }
    this.field.attach();
    this.lastTime = context.time;
  }

  reset(): void {
    for (const wave of this.waves) wave.active = false;
    this.pendingSpawns.length = 0;
    this.field?.clearAll();
    this.accumulatedDistortion = 0;
    this.radiusMultiplier = 1;
    this.frozen = 0;
    this.spawnAccumulator = 0;
    this.wavesDrawn = 0;
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
  }

  handleSignal(event: VisualSignalEvent): void {
    // Recorded, not acted on: `update` is where the frame's work happens, and a
    // signal can arrive at any point between two frames.
    //
    // Only fired for layers with no explicit mapping for these signals; a recipe
    // that maps them to actions gets the actions instead, via `applyMutation`.
    if (event.name === 'NOTE_HIT' || event.name === 'NOTE_MISS' || event.name === 'ONSET') {
      this.queue(event.strength, event.name === 'NOTE_MISS' ? 'torn' : 'clean');
    }
  }

  /**
   * The Phase 3 entry point: an instruction rather than a value.
   *
   * Each action produces a *different kind of disturbance* rather than a better
   * or worse version of one. `SPAWN` sends a clean train; `DISTORT` bends the
   * medium everything after it has to cross; `BREAK` throws a front that arrives
   * in pieces; `SPLASH` sets off several at once, which is also the only way to
   * get interference between separate events rather than within one.
   *
   * Actions this renderer has no meaning for fall through to `default` and are
   * ignored, which is the contract.
   */
  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    // Sustained mutations arrive every frame; scaled down so a held level does
    // not accumulate like a struck event.
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    // **A sustained mutation never makes a mark.** It modulates what marks look
    // like; only a moment can create one. Mapping a level such as `TREBLE` to an
    // action that spawns would emit sixty events a second, and the pool would
    // fill with whatever material that action produces no matter how the player
    // was doing — which is precisely how a run's history stops being visible.
    const struck = !item.sustained;

    switch (item.action) {
      case 'SPAWN':
        if (struck) this.queue(item.strength, 'clean');
        break;
      case 'SPLASH':
        // Several at once, scattered. A burst reads as one gesture — and their
        // fields cross, which is where the large-scale banding comes from.
        if (struck) for (let index = 0; index < 3; index += 1) this.queue(item.strength, 'torn');
        break;
      case 'DISTORT':
      case 'TURBULENCE':
        this.accumulatedDistortion = Math.min(2.5, this.accumulatedDistortion + force * 0.55);
        if (struck) this.queue(item.strength, 'torn');
        break;
      case 'BREAK':
        this.accumulatedDistortion = Math.min(2.5, this.accumulatedDistortion + force * 0.35);
        if (struck) this.queue(item.strength, 'fracture');
        break;
      case 'SHIFT':
        // A spiral is specifically the *off-beat* mark, so a press that judged a
        // note does not make one — it already produced a mark through the hit.
        // `judged` rides on the event's detail, so the renderer reads the fact
        // rather than re-deriving it from timing it does not have.
        if (struck && item.detail.judged !== true) this.queue(item.strength, 'spiral');
        break;
      case 'ROTATE':
        // Turns the medium over: the noise the next fronts cross is re-seeded.
        this.accumulatedDistortion = Math.min(2.5, this.accumulatedDistortion + force * 0.2);
        break;
      case 'EXPAND':
        this.radiusMultiplier = Math.min(3, this.radiusMultiplier + force * 0.12);
        break;
      case 'CONTRACT':
        this.radiusMultiplier = Math.max(0.25, this.radiusMultiplier - force * 0.12);
        break;
      case 'DENSITY':
        this.spawnAccumulator += force * 0.9;
        break;
      case 'FREEZE':
        this.frozen = Math.min(1, this.frozen + force * 0.5);
        break;
      case 'RELEASE':
        this.frozen = Math.max(0, this.frozen - force * 0.5);
        break;
      case 'REMOVE':
        for (const wave of this.waves) {
          if (wave.active && context.random.bool(0.3 * item.strength)) wave.active = false;
        }
        break;
      case 'RANDOMIZE':
        for (const wave of this.waves) {
          if (wave.active) wave.seed = context.random.next() * 100;
        }
        break;
      default:
        // MERGE, BLEED, FADE, OPACITY, INTENSITY: no meaning here, ignored.
        break;
    }
  }

  private queue(strength: number, kind: Kind): void {
    if (this.pendingSpawns.length >= this.capacity) return;
    this.pendingSpawns.push({ strength, kind });
  }

  /**
   * A wave, as a wave.
   *
   * The one material where the event maps onto the physics directly, so this
   * is the shortest of the six: the disturbance becomes a front at that
   * point, and everything that made the field a field in Phase 6A — the
   * constant-speed propagation, the crest train, the two sources — applies
   * to it unchanged.
   *
   * A second, weaker front is sent from just ahead of the first along the
   * direction of travel. That is what puts *this* event's interference in
   * the frame rather than relying on it happening to cross an older one.
   */
  spawnWave(event: WaveEvent): void {
    const stream = new VisualRandom(event.seed);
    const kind: Kind =
      event.style === 'torn' ? 'fracture'
      : event.style === 'stray' ? 'spiral'
      : event.style === 'swell' ? 'clean'
      : 'clean';
    const force = clamp01(event.strength);

    // A wave that shares its disturbance with a mark does not start at the
    // centre of it — it starts where the mark meets the surface. Offsetting
    // the origin outward is what makes the front read as *caused by* the ink
    // rather than as something that happened to be drawn in the same place.
    // The name is read from the group; this renderer does not know what a
    // `SumiRenderer` is and must not.
    const bornAtAnEdge =
      event.collision !== undefined &&
      event.collision.materials.some((id) => id === 'SUMI' || id === 'INK_PAINT');
    const edge = bornAtAnEdge ? 0.13 * event.scale : 0;

    this.pendingSpawns.push({
      strength: force,
      kind,
      at: {
        x: event.x + Math.cos(event.direction) * edge,
        y: event.y + Math.sin(event.direction) * edge,
      },
      stream,
      scale: event.scale,
    });

    // The companion, offset along the way it was going.
    const offset = 0.16 * event.scale;
    this.pendingSpawns.push({
      strength: force * 0.7,
      kind: kind === 'fracture' ? 'torn' : kind,
      at: {
        x: event.x + Math.cos(event.direction) * offset,
        y: event.y + Math.sin(event.direction) * offset,
      },
      stream,
      scale: event.scale * 0.8,
    });
    this.wavesDrawn += 1;
  }

  update(context: VisualFrameContext): void {
    const field = this.field;
    if (field === null || !this.enabled) return;

    const delta = Math.min(0.1, Math.max(0, context.time - this.lastTime));
    this.lastTime = context.time;
    const signals = context.signals;
    this.parameters = context.layer.parameters;

    // Damage heals, slowly. Without this a single bad passage would bend every
    // wave for the rest of the run.
    this.accumulatedDistortion = Math.max(0, this.accumulatedDistortion - delta * 0.22);

    // A steady rate of small disturbances, so a quiet passage is still water
    // rather than an empty frame.
    const spawnRate = numeric(this.parameters.spawnRate, 0.9) * (0.35 + signals.AMPLITUDE);
    this.spawnAccumulator += delta * spawnRate;
    while (this.spawnAccumulator >= 1) {
      this.spawnAccumulator -= 1;
      this.queue(0.35 + signals.BASS * 0.4, 'clean');
    }

    for (const request of this.pendingSpawns) this.spawn(context, request);
    this.pendingSpawns.length = 0;

    const opacity = context.layer.opacity * context.layer.intensity;
    const flow = 1 - this.frozen;

    let live = 0;
    for (const wave of this.waves) {
      if (!wave.active) continue;
      const age = (context.time - wave.born) / wave.lifetime;
      if (age >= 1) {
        wave.active = false;
        continue;
      }

      // Constant speed. This is the whole difference between a wave and a ring
      // that is being inflated: the front covers the same distance every second
      // however long it has been travelling.
      wave.radius += wave.speed * delta * flow;

      // Energy spreads around a circumference that grows with the radius, so the
      // amplitude falls as one over its square root. Not an opacity curve — the
      // wave gets quieter because it is bigger.
      const spreading = Math.sqrt(wave.startRadius / Math.max(wave.radius, 1e-4));
      // Only the very end of a life releases what is left, so a train does not
      // spend its whole life fading.
      const release = age > 0.82 ? 1 - (age - 0.82) / 0.18 : 1;
      const amplitude = wave.amplitude * spreading * release * opacity;
      if (amplitude <= 0.002) continue;

      // Distortion is the medium's, not the event's, so a front that set out
      // through calm water and is still travelling when the water is disturbed
      // arrives bent.
      const deform = Math.min(0.85, wave.deform + this.accumulatedDistortion * 0.18);

      const tone = waveTone(wave.hue, wave.kind);
      // The quad has to hold the whole field, and the front sits at 0.9 of it.
      const scale = (wave.radius / 0.9) * this.radiusMultiplier;

      field.write(
        live,
        [wave.x, wave.y, scale, 0],
        [wave.born, wave.lifetime, wave.seed, KIND_CODE[wave.kind]],
        [tone[0], tone[1], tone[2], clamp01(amplitude)],
        [wave.wavenumber, wave.separation, deform, wave.trainLength],
      );
      live += 1;
      if (live >= field.capacity) break;
    }

    field.setTime(context.time);
    field.commit(live);
  }

  private spawn(
    context: VisualFrameContext,
    request: {
      strength: number;
      kind: Kind;
      at?: { x: number; y: number };
      stream?: VisualRandom;
      scale?: number;
    },
  ): void {
    const wave = this.waves.find((candidate) => !candidate.active);
    if (wave === undefined) return;
    const { strength, kind } = request;
    // A wave brings its own stream, so the same event sends the same front
    // however much work this layer had already done when it arrived.
    const random = request.stream ?? context.random;
    const parameters = this.parameters;
    const reach = this.fieldRadius * numeric(parameters.reach, 0.55);

    wave.active = true;
    wave.born = context.time;
    wave.seed = random.next() * 100;
    wave.kind = kind;
    // Field units in, world units out.
    wave.x = request.at === undefined ? random.signed() * reach : request.at.x * this.fieldRadius;
    wave.y =
      request.at === undefined ? random.signed() * reach * 0.8 : request.at.y * this.fieldRadius;

    wave.startRadius = this.fieldRadius * random.range(0.06, 0.11);
    wave.radius = wave.startRadius;
    wave.speed =
      this.fieldRadius *
      numeric(parameters.growth, 0.55) *
      (0.6 + context.signals.BASS) *
      random.range(0.7, 1.3);
    wave.lifetime = numeric(parameters.lifetime, 2.6) * random.range(0.8, 1.5);

    // The wavelength. A tighter train reads as a smaller, sharper disturbance;
    // this is the parameter that separates a tap from a swell.
    wave.wavenumber = random.range(4.5, 9) * (0.7 + strength * 0.6);
    // Two sources have to be at least a couple of wavelengths apart before
    // their nodal lines are wide enough to see; closer than that the pair is
    // indistinguishable from a single source and the interference is real but
    // invisible.
    wave.separation = random.range(0.16, 0.44);
    // Long enough to hold three or four crests. At a third of this only the
    // leading crest survived the envelope and every event was one ring again.
    wave.trainLength = random.range(0.36, 0.7);

    switch (kind) {
      case 'torn':
        wave.deform = random.range(0.35, 0.7);
        wave.separation = random.range(0.3, 0.55);
        break;
      case 'spiral':
        wave.deform = random.range(0.1, 0.28);
        wave.wavenumber *= 0.8;
        wave.trainLength = random.range(0.3, 0.55);
        break;
      case 'fracture':
        wave.deform = random.range(0.5, 0.95);
        wave.separation = random.range(0.05, 0.2);
        wave.lifetime *= 0.8;
        break;
      default:
        wave.deform = random.range(0.03, 0.14);
        break;
    }

    wave.amplitude = clamp01((0.3 + strength * 0.34) * random.range(0.75, 1.15));
    wave.speed *= request.scale ?? 1;
    const hue = numeric(parameters.hue, 0.52);
    wave.hue = (hue + random.signed() * 0.06 + 1) % 1;
  }

  render(): void {
    // Nothing: the field is in the scene, and the stage's single render draws it
    // with everything else. A second draw call here would be a second pass.
  }

  /**
   * More events, finer noise and sharper crests, when the budget allows.
   *
   * Only the capacity is fixed at build time — the field is allocated once and
   * changing quality mid-run must not reallocate it during play.
   */
  setQuality(quality: QualityContext): void {
    if (this.field === null) {
      this.capacity = Math.max(1, Math.round(this.baseCapacity * quality.capacityScale));
    }
    this.detail = Math.max(2, Math.min(6, Math.round(2 + quality.iterationScale * 1.4)));
    this.sharpness = quality.preset === 'LIVE' ? 1.15 : quality.preset === 'HIGH' ? 1 : 0.88;
    this.field?.setUniform('uDetail', this.detail);
    this.field?.setUniform('uSharpness', this.sharpness);
  }

  setParameters(parameters: Readonly<Record<string, number | string | boolean>>): void {
    this.parameters = parameters;
    const blend = parameters.blendMode;
    if (typeof blend === 'string' && this.field !== null) {
      this.field.material.blending = blendingFor(blend);
      this.field.material.needsUpdate = true;
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.field !== null) this.field.visible = enabled;
    if (!enabled) this.reset();
  }

  dispose(): void {
    this.field?.dispose();
    this.field = null;
    this.waves = [];
  }

  get metrics(): {
    readonly live: number;
    readonly capacity: number;
    readonly distortion: number;
    readonly frozen: number;
    /** Mean front radius in field radii: how far the medium has carried them. */
    readonly propagation: number;
    /** Mean source separation: how strongly the live fields interfere. */
    readonly interference: number;
    readonly waves: number;
    readonly mutations: Readonly<Record<string, number>>;
  } {
    let radius = 0;
    let separation = 0;
    let counted = 0;
    for (const wave of this.waves) {
      if (!wave.active) continue;
      radius += wave.radius / this.fieldRadius;
      separation += wave.separation;
      counted += 1;
    }
    return {
      live: this.field?.liveCount ?? 0,
      capacity: this.capacity,
      distortion: Number(this.accumulatedDistortion.toFixed(3)),
      frozen: Number(this.frozen.toFixed(3)),
      propagation: Number((counted === 0 ? 0 : radius / counted).toFixed(3)),
      interference: Number((counted === 0 ? 0 : separation / counted).toFixed(3)),
      waves: this.wavesDrawn,
      mutations: { ...this.mutationCounts },
    };
  }
}

/**
 * A wave's colour.
 *
 * Held low: these blend additively so that separate events sum where they cross,
 * and a palette bright enough on its own would put the crossings over the bloom
 * threshold and turn the field into glare. The kind shifts the hue rather than
 * the value — a torn front is not a dimmer clean one, it is a different colour
 * of disturbance.
 */
function waveTone(hue: number, kind: Kind): [number, number, number] {
  const shifted =
    kind === 'torn' ? hue - 0.08
    : kind === 'fracture' ? hue - 0.16
    : kind === 'spiral' ? hue + 0.1
    : hue;
  return hslToRgb(((shifted % 1) + 1) % 1, 0.62, kind === 'clean' ? 0.42 : 0.38);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;
  const sector = Math.floor(h * 6) % 6;
  const rgb: [number, number, number] =
    sector === 0 ? [c, x, 0]
    : sector === 1 ? [x, c, 0]
    : sector === 2 ? [0, c, x]
    : sector === 3 ? [0, x, c]
    : sector === 4 ? [x, 0, c]
    : [c, 0, x];
  return [rgb[0] + m, rgb[1] + m, rgb[2] + m];
}

function emptyWave(): Wave {
  return {
    active: false, x: 0, y: 0, born: 0, lifetime: 1, radius: 0, speed: 1, amplitude: 0.3,
    startRadius: 0.1, wavenumber: 3, separation: 0.12, deform: 0.1, trainLength: 0.3,
    hue: 0.52, kind: 'clean', seed: 0,
  };
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
